import type { CoopLogicPlugin } from '../../core/types/plugin.base.types';
import type { CoopCrosswordBoard, CoopPayload, CoopRuntime, CoopTurnRef } from '../../core/types/payloads/coop.types';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import {
  buildCrosswordGrid,
  isClueComplete,
  isPuzzleComplete,
  nextEmptyCellInClue,
  resolveActiveClue,
  validateCrosswordBoard,
} from './crossword.engine';

function validateClue(raw: unknown, index: number) {
  if (!raw || typeof raw !== 'object') throw new Error(`Clue ${index} invalid`);
  const c = raw as Record<string, unknown>;
  const direction = String(c.direction || 'across');
  if (direction !== 'across' && direction !== 'down') throw new Error(`Clue ${index}: invalid direction`);
  const length = Number(c.length);
  const answer = String(c.answer || '').toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');
  if (!answer || answer.length !== length) throw new Error(`Clue ${index}: answer length mismatch`);
  return {
    id: String(c.id || `clue_${index + 1}`),
    direction: direction as 'across' | 'down',
    row: Number(c.row),
    col: Number(c.col),
    length,
    clue: String(c.clue || ''),
    answer,
  };
}

export function validateCoopPayload(raw: unknown): CoopPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Coop payload must be an object');
  const data = raw as Partial<CoopPayload>;
  if (!data.title) throw new Error('Coop requires title');
  if (!data.board || typeof data.board !== 'object') throw new Error('Coop requires board');

  const board = data.board as CoopPayload['board'];
  if (board.kind === 'crossword') {
    const crossword: CoopCrosswordBoard = {
      kind: 'crossword',
      rows: Number(board.rows) || 5,
      cols: Number(board.cols) || 5,
      clues: Array.isArray(board.clues) ? board.clues.map(validateClue) : [],
    };
    validateCrosswordBoard(crossword);
    return {
      title: String(data.title),
      board: crossword,
      settings: {
        turnDurationSec: data.settings?.turnDurationSec,
        turnUnit: data.settings?.turnUnit ?? 'team',
      },
    };
  }

  if (board.kind === 'hangman') {
    return {
      title: String(data.title),
      board: {
        kind: 'hangman',
        word: String(board.word || '').toUpperCase(),
        hint: board.hint ? String(board.hint) : undefined,
        maxErrors: Number(board.maxErrors) || 6,
      },
      settings: {
        turnDurationSec: data.settings?.turnDurationSec,
        turnUnit: data.settings?.turnUnit ?? 'team',
      },
    };
  }

  if (board.kind === 'chain') {
    const segments = Array.isArray(board.segments)
      ? board.segments.map((s, i) => {
          const seg = s as Record<string, unknown>;
          const choices = Array.isArray(seg.choices) ? seg.choices.map(String) : [];
          return {
            id: String(seg.id || `seg_${i + 1}`),
            textBefore: String(seg.textBefore || ''),
            textAfter: String(seg.textAfter || ''),
            correct: String(seg.correct || choices[0] || ''),
            choices: choices.length >= 2 ? choices : [String(seg.correct || 'y'), 'pero', 'sin embargo'],
          };
        })
      : [];
    if (segments.length < 1) throw new Error('Chain requires at least one segment');
    return {
      title: String(data.title),
      board: { kind: 'chain', intro: board.intro ? String(board.intro) : undefined, segments },
      settings: { turnDurationSec: data.settings?.turnDurationSec, turnUnit: data.settings?.turnUnit ?? 'participant' },
    };
  }

  if (board.kind === 'anagram') {
    const answer = String(board.answer || '').toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');
    if (!answer) throw new Error('Anagram requires answer');
    const letterPool = Array.isArray(board.letterPool)
      ? board.letterPool.map((l) => String(l).toUpperCase().slice(0, 1))
      : answer.split('');
    return {
      title: String(data.title),
      board: {
        kind: 'anagram',
        hint: String(board.hint || 'Forma la palabra con las letras'),
        answer,
        letterPool,
      },
      settings: { turnDurationSec: data.settings?.turnDurationSec, turnUnit: data.settings?.turnUnit ?? 'team' },
    };
  }

  if (board.kind === 'draw-guess') {
    const mode = String(board.sketchMode || 'concept');
    return {
      title: String(data.title),
      board: {
        kind: 'draw-guess',
        prompt: String(board.prompt || 'Dibuja para que adivinen'),
        sketchMode: mode === 'melody' ? 'melody' : 'concept',
      },
      settings: { turnDurationSec: data.settings?.turnDurationSec, turnUnit: data.settings?.turnUnit ?? 'participant' },
    };
  }

  return {
    title: String(data.title),
    board: data.board,
    settings: data.settings,
  };
}

function buildTurnOrder(_payload: CoopPayload, participantIds: string[]): string[] {
  return [...participantIds];
}

function resolveCurrentTurn(
  turnOrder: string[],
  turnIndex: number,
  turnUnit: 'participant' | 'team',
): CoopTurnRef | null {
  const actorId = turnOrder[turnIndex];
  if (!actorId) return null;
  return turnUnit === 'team' ? { teamId: actorId } : { participantId: actorId };
}

export function createInitialCoopRuntime(payload: CoopPayload): CoopRuntime {
  const base: CoopRuntime = {
    boardState: {},
    turnOrder: [],
    currentTurnIndex: 0,
    currentTurn: null,
    filledCells: {},
    completedIds: [],
    selectedCell: null,
    activeClueId: null,
    won: false,
    errors: 0,
    history: [],
  };
  if (payload.board.kind === 'chain') {
    return { ...base, chainFills: {}, currentSegmentIndex: 0 };
  }
  if (payload.board.kind === 'anagram') {
    return { ...base, builtWord: '', usedLetterIndices: [] };
  }
  if (payload.board.kind === 'draw-guess') {
    return { ...base, strokes: [], guesses: {}, drawerId: null };
  }
  return base;
}

export function canCoopAct(ctx: PluginContext<'coop'>, participant: Participant): boolean {
  const turn = ctx.runtime.currentTurn;
  if (!turn) return false;
  const unit = ctx.payload.settings?.turnUnit ?? 'team';
  if (unit === 'team') return !!participant.teamId && turn.teamId === participant.teamId;
  return turn.participantId === participant.participantId;
}

function isCrossword(ctx: PluginContext<'coop'>): ctx is PluginContext<'coop'> & { payload: CoopPayload & { board: CoopCrosswordBoard } } {
  return ctx.payload.board.kind === 'crossword';
}

function advanceTurnRuntime(ctx: PluginContext<'coop'>): CoopRuntime {
  const nextIndex = (ctx.runtime.currentTurnIndex + 1) % Math.max(ctx.runtime.turnOrder.length, 1);
  const unit = ctx.payload.settings?.turnUnit ?? 'team';
  return {
    ...ctx.runtime,
    currentTurnIndex: nextIndex,
    currentTurn: resolveCurrentTurn(ctx.runtime.turnOrder, nextIndex, unit),
  };
}

function handleCrosswordFill(
  ctx: PluginContext<'coop'>,
  participant: Participant,
  cellKey: string,
  letter: string,
): { runtime: CoopRuntime; rejected?: boolean; rejectReason?: string } {
  const board = ctx.payload.board as CoopCrosswordBoard;
  const meta = buildCrosswordGrid(board);
  const normalized = letter.toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');

  if (!normalized) return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_LETTER' };
  if (meta.blocked.has(cellKey)) return { runtime: ctx.runtime, rejected: true, rejectReason: 'BLOCKED_CELL' };
  if (ctx.runtime.filledCells[cellKey]) return { runtime: ctx.runtime, rejected: true, rejectReason: 'CELL_FILLED' };

  const expected = meta.solution[cellKey];
  if (!expected) return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_CELL' };
  if (normalized !== expected) {
    return {
      runtime: {
        ...ctx.runtime,
        errors: (ctx.runtime.errors ?? 0) + 1,
        history: [
          ...ctx.runtime.history,
          { ts: Date.now(), actorId: participant.participantId, action: 'FILL_CELL_WRONG', data: { cellKey, letter: normalized } },
        ],
      },
      rejected: true,
      rejectReason: 'WRONG_LETTER',
    };
  }

  const filledCells = { ...ctx.runtime.filledCells, [cellKey]: normalized };
  const completedIds = [...ctx.runtime.completedIds];
  let newlyCompleted: string | null = null;

  for (const clue of board.clues) {
    if (!completedIds.includes(clue.id) && isClueComplete(clue.id, meta, filledCells)) {
      completedIds.push(clue.id);
      newlyCompleted = clue.id;
    }
  }

  const won = isPuzzleComplete(board, meta, filledCells);
  let runtime: CoopRuntime = {
    ...ctx.runtime,
    filledCells,
    completedIds,
    won,
    selectedCell: cellKey,
    activeClueId: resolveActiveClue(board, meta, cellKey, ctx.runtime.activeClueId),
    history: [
      ...ctx.runtime.history,
      { ts: Date.now(), actorId: participant.participantId, action: 'FILL_CELL', data: { cellKey, letter: normalized } },
    ],
  };

  if (newlyCompleted && ctx.runtime.turnOrder.length > 1) {
    runtime = advanceTurnRuntime({ ...ctx, runtime });
  }

  return { runtime };
}

function handleCrosswordSelect(
  ctx: PluginContext<'coop'>,
  participant: Participant,
  cellKey: string,
  allowReadOnly: boolean,
): { runtime: CoopRuntime; rejected?: boolean; rejectReason?: string } {
  const board = ctx.payload.board as CoopCrosswordBoard;
  const meta = buildCrosswordGrid(board);
  if (meta.blocked.has(cellKey)) return { runtime: ctx.runtime, rejected: true, rejectReason: 'BLOCKED_CELL' };

  if (!allowReadOnly && !canCoopAct(ctx, participant)) {
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
  }

  return {
    runtime: {
      ...ctx.runtime,
      selectedCell: cellKey,
      activeClueId: resolveActiveClue(board, meta, cellKey, ctx.runtime.activeClueId),
    },
  };
}

export const coopLogic: CoopLogicPlugin = {
  mode: 'coop',

  validatePayload: validateCoopPayload,
  createInitialRuntime: createInitialCoopRuntime,
  canAct: canCoopAct,

  advanceTurn(ctx) {
    return { runtime: advanceTurnRuntime(ctx) };
  },

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }

    if (isCrossword(ctx)) {
      if (input.action === 'SELECT_CELL') {
        const data = input.data as { cellKey?: string };
        if (!data.cellKey) return { runtime: ctx.runtime };
        const canAct = canCoopAct(ctx, participant);
        return handleCrosswordSelect(ctx, participant, data.cellKey, !canAct);
      }

      if (input.action === 'SELECT_CLUE') {
        const data = input.data as { clueId?: string };
        const clueId = data.clueId;
        if (!clueId) return { runtime: ctx.runtime };
        const meta = buildCrosswordGrid(ctx.payload.board);
        const empty = nextEmptyCellInClue(clueId, meta, ctx.runtime.filledCells);
        const cell = empty ?? meta.clueCells[clueId]?.[0];
        if (!cell) return { runtime: ctx.runtime };
        return {
          runtime: {
            ...ctx.runtime,
            activeClueId: clueId,
            selectedCell: cell,
          },
        };
      }

      if (input.action === 'FILL_CELL') {
        if (!canCoopAct(ctx, participant)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
        }
        if (ctx.runtime.won) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'PUZZLE_COMPLETE' };
        }
        const data = input.data as { cellKey?: string; letter?: string };
        if (!data.cellKey || !data.letter) return { runtime: ctx.runtime };
        return handleCrosswordFill(ctx, participant, data.cellKey, data.letter);
      }
    }

    if (ctx.payload.board.kind === 'chain') {
      if (input.action === 'PICK_CONNECTOR') {
        if (!canCoopAct(ctx, participant)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
        }
        const board = ctx.payload.board;
        const idx = ctx.runtime.currentSegmentIndex ?? 0;
        const segment = board.segments[idx];
        if (!segment) return { runtime: ctx.runtime, rejected: true, rejectReason: 'CHAIN_COMPLETE' };
        const choice = String((input.data as { connector?: string })?.connector ?? '');
        const chainFills = { ...ctx.runtime.chainFills, [segment.id]: choice };
        const correct = choice === segment.correct;
        const nextIdx = idx + 1;
        const won = nextIdx >= board.segments.length;
        let runtime: CoopRuntime = {
          ...ctx.runtime,
          chainFills,
          currentSegmentIndex: won ? idx : nextIdx,
          won,
          errors: correct ? ctx.runtime.errors : (ctx.runtime.errors ?? 0) + 1,
          history: [
            ...ctx.runtime.history,
            { ts: Date.now(), actorId: participant.participantId, action: 'PICK_CONNECTOR', data: { segmentId: segment.id, choice, correct } },
          ],
        };
        if (!won && ctx.runtime.turnOrder.length > 1) {
          runtime = advanceTurnRuntime({ ...ctx, runtime });
        }
        return { runtime };
      }
    }

    if (ctx.payload.board.kind === 'anagram') {
      const board = ctx.payload.board;
      if (input.action === 'ADD_LETTER') {
        if (!canCoopAct(ctx, participant)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
        }
        const letterIndex = Number((input.data as { letterIndex?: number })?.letterIndex);
        const used = ctx.runtime.usedLetterIndices ?? [];
        if (used.includes(letterIndex)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'LETTER_USED' };
        }
        const letter = board.letterPool[letterIndex];
        if (!letter) return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_LETTER' };
        return {
          runtime: {
            ...ctx.runtime,
            builtWord: (ctx.runtime.builtWord ?? '') + letter,
            usedLetterIndices: [...used, letterIndex],
          },
        };
      }
      if (input.action === 'UNDO_LETTER') {
        if (!canCoopAct(ctx, participant)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
        }
        const used = [...(ctx.runtime.usedLetterIndices ?? [])];
        if (used.length === 0) return { runtime: ctx.runtime };
        used.pop();
        const builtWord = (ctx.runtime.builtWord ?? '').slice(0, -1);
        return { runtime: { ...ctx.runtime, builtWord, usedLetterIndices: used } };
      }
      if (input.action === 'SUBMIT_WORD') {
        if (!canCoopAct(ctx, participant)) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
        }
        const word = (ctx.runtime.builtWord ?? '').toUpperCase();
        const won = word === board.answer;
        return {
          runtime: {
            ...ctx.runtime,
            won,
            history: [
              ...ctx.runtime.history,
              { ts: Date.now(), actorId: participant.participantId, action: 'SUBMIT_WORD', data: { word, won } },
            ],
          },
        };
      }
    }

    if (ctx.payload.board.kind === 'draw-guess') {
      const drawerId = ctx.runtime.drawerId ?? ctx.runtime.turnOrder[ctx.runtime.currentTurnIndex];
      const isDrawer = participant.participantId === drawerId;

      if (input.action === 'STROKE') {
        if (!isDrawer) return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_DRAWER' };
        const points = (input.data as { points?: Array<{ x: number; y: number }> })?.points;
        if (!Array.isArray(points) || points.length < 2) return { runtime: ctx.runtime };
        return {
          runtime: {
            ...ctx.runtime,
            strokes: [...(ctx.runtime.strokes ?? []), { points, color: '#1e293b' }],
          },
        };
      }
      if (input.action === 'SUBMIT_GUESS') {
        if (isDrawer) return { runtime: ctx.runtime, rejected: true, rejectReason: 'DRAWER_CANNOT_GUESS' };
        const text = String((input.data as { text?: string })?.text ?? '').trim().slice(0, 80);
        if (!text) return { runtime: ctx.runtime, rejected: true, rejectReason: 'EMPTY_GUESS' };
        return {
          runtime: {
            ...ctx.runtime,
            guesses: { ...(ctx.runtime.guesses ?? {}), [participant.participantId]: text },
          },
        };
      }
    }

    if (input.action === 'FILL_CELL') {
      if (!canCoopAct(ctx, participant)) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_YOUR_TURN' };
      }
      const data = input.data as { cellKey?: string; letter?: string };
      if (!data.cellKey || !data.letter) return { runtime: ctx.runtime };
      const filledCells = { ...ctx.runtime.filledCells, [data.cellKey]: data.letter.toUpperCase() };
      return {
        runtime: {
          ...ctx.runtime,
          filledCells,
          history: [
            ...ctx.runtime.history,
            { ts: Date.now(), actorId: participant.participantId, action: input.action, data },
          ],
        },
      };
    }

    return { runtime: ctx.runtime };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'INIT_TURNS') {
      const ids = Array.isArray(action.data) ? (action.data as string[]) : [];
      const turnOrder = buildTurnOrder(ctx.payload, ids);
      const unit = ctx.payload.settings?.turnUnit ?? 'team';
      const drawerId = ctx.payload.board.kind === 'draw-guess' ? turnOrder[0] : ctx.runtime.drawerId;
      return {
        runtime: {
          ...ctx.runtime,
          turnOrder,
          currentTurnIndex: 0,
          currentTurn: resolveCurrentTurn(turnOrder, 0, unit),
          drawerId: drawerId ?? null,
        },
      };
    }
    if (action.action === 'ADVANCE_TURN') {
      return coopLogic.advanceTurn(ctx);
    }
    if (action.action === 'RESET_BOARD') {
      return { runtime: createInitialCoopRuntime(ctx.payload) };
    }
    return { runtime: ctx.runtime };
  },
};

export { demoCoopCrosswordPayload as demoCoopPayload } from './coop.demos';
